import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  User, MapPin, Phone, Mail, FileText, Image, Share2, Search,
  Loader2, Check, ChevronRight, ChevronLeft, ArrowLeft, Globe, Building2, Camera,
  Plus, X, Sparkles, Eye, MessageCircle, ToggleLeft, ToggleRight, Zap, AlertTriangle,
  Dog, PawPrint, Baby, Trash2, Edit, Calendar, Users, Heart, Layers, ExternalLink, EyeOff, MoreVertical,
  Crown
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { API } from '@/utils/api';
import { getImageUrl } from '@/utils/imageUrl';
import { miotCreate, miotEdit } from '@/routes/routeMap';

import StudioShell from '@/components/studio/StudioShell';
import AssistantPanel from '@/components/AssistantPanel';
import { useAuth } from '@/context/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTranslation } from 'react-i18next';

/* ─── GENERATE BUTTON ─── */
function GenerateBtn({ field, profileType, onGenerated }) {
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('token');
  
  const generate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/assistant/generate-profile-text`, {
        field,
        profile_type: profileType,
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data?.text) onGenerated(res.data.text);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={generate}
      disabled={loading}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold bg-[#F47A3B]/10 text-[#F47A3B] hover:bg-[#F47A3B]/20 transition-colors disabled:opacity-50"
      data-testid={`generate-${field}`}
    >
      {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
      {loading ? 'Generuję...' : 'Wygeneruj'}
    </button>
  );
}

const getTabs = (isHodowla) => isHodowla
  ? [
      { id: 'basic', label: 'Hodowla', icon: User },
      { id: 'breeds', label: 'Rasy', icon: Dog },
      { id: 'studs', label: 'Reproduktory', icon: Crown },
      { id: 'dogs', label: 'Suki / Psy hodowlane', icon: PawPrint },
      { id: 'offer', label: 'Mioty', icon: Baby },
      { id: 'photos', label: 'Galeria', icon: Image },
      { id: 'responses', label: 'Odpowiedzi', icon: MessageCircle },
    ]
  : [
      { id: 'basic', label: 'Dane podstawowe', icon: User },
      { id: 'offer', label: 'Oferta', icon: Layers },
      { id: 'photos', label: 'Galeria', icon: Image },
      { id: 'responses', label: 'Odpowiedzi', icon: MessageCircle },
    ];

/* ─── DYNAMIC RIGHT COLUMN CONTENT ─── */
const SECTION_CONTEXT = {
  basic: {
    title: 'Pokaż, kim jesteś i zbuduj zaufanie',
    desc: 'Dane podstawowe, opis i social media — to fundament Twojej strony i widoczności.',
    benefits: ['Klient widzi kto stoi za ofertą', 'Twoja strona wygląda profesjonalnie', 'Google wyżej pozycjonuje profile z danymi'],
    action: 'Uzupełnij dane kontaktowe, opis i social media',
  },
  breeds: {
    title: 'Rasy, które hodujesz',
    desc: 'Posty, grupy i oferta są dopasowane do Twoich ras. Główna rasa jest używana w kampaniach.',
    benefits: ['Lepiej dopasowane posty', 'Trafniejsze grupy docelowe', 'Oferta per rasa w przyszłości'],
    action: 'Dodaj rasy, których hodujesz',
  },
  offer: {
    title: 'To właśnie tę ofertę klient ma wybrać',
    desc: 'Oferta, mioty i szczegóły — to zasila stronę, marketing i odpowiedzi.',
    benefits: ['Lepsza sprzedaż', 'Lepsze SEO', 'Konkretniejsze odpowiedzi'],
    action: 'Uzupełnij opis oferty i dodaj mioty',
  },
  dogs: {
    title: 'Twoje suki i psy hodowlane to wizytówka',
    desc: 'Dodaj psy ze zdjęciami — klienci chcą widzieć rodziców szczeniąt.',
    benefits: ['Większe zaufanie klientów', 'Atrakcyjniejsza strona', 'Lepsze wyniki w wyszukiwarce'],
    action: 'Dodaj suki i psy ze zdjęciami i rodowodami',
  },
  studs: {
    title: 'Reproduktory — Twoja przewaga w rozmowach',
    desc: 'Dodaj reproduktory z osiągnięciami i rodowodem — to magnes na zapytania o krycie i szczenięta.',
    benefits: ['Więcej zapytań o krycie', 'Mocniejszy wizerunek hodowli', 'Lepsze pozycjonowanie reproduktorów'],
    action: 'Dodaj reproduktory ze zdjęciami i tytułami',
  },
  photos: {
    title: 'Zdjęcia budują zaufanie i pomagają sprzedawać',
    desc: 'Te zdjęcia są używane na Twojej stronie, w marketingu i odpowiedziach do klientów.',
    benefits: ['Większe zaufanie', 'Lepsza prezentacja oferty', 'Mocniejsze posty i odpowiedzi'],
    action: 'Dodaj zdjęcia pokazujące jakość i ofertę',
  },
  responses: {
    title: 'Jak odpowiadasz klientom',
    desc: 'Gdy klient wyśle zapytanie, przygotujemy gotową odpowiedź dopasowaną do Twojej oferty.',
    benefits: ['Szybsza reakcja', 'Lepsze dopasowanie', 'Większa szansa sprzedaży'],
    action: 'Uzupełnij ofertę, wyróżniki i ton odpowiedzi',
    micro: 'Działa w: WhatsApp, e-mail i szybkich odpowiedziach.',
  },
};

function Field({ label, why, children, fromInterview }) {
  return (
    <div className="mb-5">
      <div className="flex items-center gap-2 mb-1.5">
        <label className="text-sm font-medium text-zinc-900">{label}</label>
        {fromInterview && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-[10px] font-medium text-emerald-600" data-testid="interview-badge">
            <Check className="w-2.5 h-2.5" />
            z wywiadu
          </span>
        )}
      </div>
      {why && <p className="text-xs text-zinc-500 mb-2">{why}</p>}
      {children}
    </div>
  );
}

function Input({ value, onChange, placeholder, type = 'text', ...props }) {
  return (
    <input
      type={type}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-white border border-zinc-200 rounded-xl px-4 py-3 text-zinc-900 text-sm placeholder-[#94A3B8] focus:border-[#F47A3B]/50 focus:ring-1 focus:ring-[#F47A3B]/20 outline-none transition"
      {...props}
    />
  );
}

function Textarea({ value, onChange, placeholder, rows = 4 }) {
  return (
    <textarea
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className="w-full bg-white border border-zinc-200 rounded-xl px-4 py-3 text-zinc-900 text-sm placeholder-[#94A3B8] focus:border-[#F47A3B]/50 focus:ring-1 focus:ring-[#F47A3B]/20 outline-none transition resize-none"
    />
  );
}

function TagsInput({ value = [], onChange, placeholder }) {
  const [input, setInput] = useState('');
  const add = () => {
    const trimmed = input.trim();
    if (trimmed && !value.includes(trimmed)) {
      onChange([...value, trimmed]);
      setInput('');
    }
  };
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-2">
        {value.map((tag, i) => (
          <span key={i} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#F47A3B]/10 border border-[#F47A3B]/20 rounded-full text-[#F47A3B] text-xs">
            {tag}
            <button onClick={() => onChange(value.filter((_, j) => j !== i))} className="hover:text-zinc-900"><X className="w-3 h-3" /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), add())}
          placeholder={placeholder}
          className="flex-1 bg-white border border-zinc-200 rounded-xl px-4 py-2.5 text-zinc-900 text-sm placeholder-[#94A3B8] focus:border-[#F47A3B]/50 outline-none"
        />
        <Button onClick={add} size="sm" variant="outline" className="border-zinc-200 text-zinc-500 rounded-xl"><Plus className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}

/* ─── MINI PREVIEW ─── */
function StickyPreview({ profile }) {
  const photo = getImageUrl(profile.main_photo);
  return (
    <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden" data-testid="sticky-preview">
      <div className="h-32 bg-gradient-to-br from-[#F47A3B]/10 to-[#F47A3B]/5 relative">
        {photo && <img src={photo} alt="" className="w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-white to-transparent" />
      </div>
      <div className="p-5 -mt-6 relative z-10">
        <h3 className="text-zinc-900 font-bold text-lg line-clamp-1">{profile.business_name || 'Twoja nazwa'}</h3>
        <p className="text-[#F47A3B] text-xs mt-0.5">
          {profile.business_type === 'breeder' ? 'Hodowla' : profile.business_type === 'groomer' ? 'Groomer' : 'Firma'}
        </p>
        {(profile.city || profile.voivodeship) && (
          <p className="text-zinc-500 text-xs mt-2 flex items-center gap-1"><MapPin className="w-3 h-3" />{[profile.city, profile.voivodeship].filter(Boolean).join(', ')}</p>
        )}
        {profile.description && (
          <p className="text-[#94A3B8] text-xs mt-2 line-clamp-2">{profile.description}</p>
        )}
        {profile.highlights?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {(profile.highlights.flatMap(h => typeof h === 'string' && h.includes(',') ? h.split(',').map(s => s.trim()).filter(Boolean) : [h])).slice(0, 3).map((h, i) => (
              <span key={i} className="px-2 py-0.5 bg-[#F47A3B]/10 text-[#F47A3B] text-[10px] rounded-full">{(() => { const L = { zdrowie_badania: 'Zdrowie i badania', rodowod: 'Rodowód FCI/ZKwP', warunki: 'Warunki i opieka', doswiadczenie: 'Doświadczenie', kontakt_po: 'Kontakt po odbiorze', terminy: 'Szybkie terminy', jakosc: 'Dobra jakość', opinie: 'Opinie klientów', cena: 'Cena' }; return L[h?.trim?.()?.toLowerCase?.()] || h; })()}</span>
            ))}
          </div>
        )}
        {profile.phone && <p className="text-zinc-500 text-xs mt-2 flex items-center gap-1"><Phone className="w-3 h-3" />{profile.phone}</p>}
      </div>
      <div className="px-5 pb-4">
        <Link to="/panel/moje" data-testid="preview-cta">
          <Button size="sm" className="w-full bg-[#F47A3B]/10 text-[#F47A3B] hover:bg-[#F47A3B]/20 rounded-xl text-xs">
            <Eye className="w-3.5 h-3.5 mr-1.5" /> Pełny podgląd strony
          </Button>
        </Link>
      </div>
    </div>
  );
}

/* ─── MAIN COMPONENT ─── */
/* ─── PROFILE PHOTO UPLOAD ─── */
function ProfilePhotoUpload({ currentUrl, onUploaded, testId }) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentUrl);
  const inputRef = useRef(null);
  const token = localStorage.getItem('token');

  useEffect(() => { setPreview(currentUrl); }, [currentUrl]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setPreview(URL.createObjectURL(file));
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await axios.post(`${API}/profile/upload-photo`, form, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      onUploaded(res.data.url);
    } catch {
      setPreview(currentUrl);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex items-center gap-4">
      <div className="relative w-24 h-24 rounded-xl bg-zinc-50 border border-zinc-200 overflow-hidden flex-shrink-0 group cursor-pointer" onClick={() => inputRef.current?.click()}>
        {preview ? (
          <img src={preview} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><Camera className="w-8 h-8 text-zinc-300" /></div>
        )}
        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          {uploading ? <Loader2 className="w-6 h-6 text-white animate-spin" /> : <Camera className="w-6 h-6 text-white" />}
        </div>
      </div>
      <div>
        <Button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="bg-[#F47A3B] hover:bg-[#E06A2E] text-white rounded-xl px-5 py-2.5 text-[13px] font-bold shadow-md shadow-[#F47A3B]/20"
          data-testid={testId}
        >
          {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Wgrywam...</> : <><Camera className="w-4 h-4 mr-2" />Wybierz zdjęcie</>}
        </Button>
        <p className="text-[11px] text-zinc-400 mt-1.5">JPG, PNG lub WebP. Max 10MB.</p>
      </div>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleFile} className="hidden" />
    </div>
  );
}

/* ─── GALLERY UPLOAD (multi) ─── */
function GalleryUpload({ currentUrls, onChanged }) {
  const [urls, setUrls] = useState(currentUrls || []);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const token = localStorage.getItem('token');

  useEffect(() => { setUrls(currentUrls || []); }, [currentUrls]);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const newUrls = [...urls];
    for (const file of files) {
      const form = new FormData();
      form.append('file', file);
      try {
        const res = await axios.post(`${API}/profile/upload-photo`, form, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        newUrls.push(res.data.url);
      } catch { /* skip failed */ }
    }
    setUrls(newUrls);
    onChanged(newUrls);
    setUploading(false);
  };

  const remove = (idx) => {
    const next = urls.filter((_, i) => i !== idx);
    setUrls(next);
    onChanged(next);
  };

  return (
    <div>
      <div className="grid grid-cols-4 gap-3 mb-3">
        {urls.map((url, i) => (
          <div key={i} className="relative w-full aspect-square rounded-xl overflow-hidden border border-zinc-200 group">
            <img src={url} alt="" className="w-full h-full object-cover" />
            <button onClick={() => remove(i)} className="absolute top-1 right-1 w-6 h-6 bg-black/60 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`gallery-remove-${i}`}>
              <X className="w-3.5 h-3.5 text-white" />
            </button>
          </div>
        ))}
        <button
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="w-full aspect-square rounded-xl border-2 border-dashed border-zinc-200 hover:border-[#F47A3B]/40 flex flex-col items-center justify-center text-zinc-400 hover:text-[#F47A3B] transition-colors"
          data-testid="gallery-add-btn"
        >
          {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <><Plus className="w-6 h-6" /><span className="text-[10px] font-medium mt-1">Dodaj</span></>}
        </button>
      </div>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={handleFiles} className="hidden" />
    </div>
  );
}

/* ─── RESPONSES TAB (Context Engine Wizard) ─── */
function ResponsesTab({ isHodowla }) {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [rp, setRp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(0);
  const saveTimer = useRef(null);

  // Fetch response profile
  useEffect(() => {
    axios.get(`${API}/response-profile`, { headers })
      .then(r => setRp(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  // Auto-save with debounce
  const save = useCallback((data) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        await axios.put(`${API}/response-profile`, data, { headers });
      } catch (e) {
        console.error('Save failed:', e);
      } finally {
        setSaving(false);
      }
    }, 800);
  }, []); // eslint-disable-line

  const update = useCallback((field, value) => {
    setRp(prev => {
      const updated = { ...prev, [field]: value };
      save({ [field]: value });
      return updated;
    });
  }, [save]);

  const toggleHighlight = (id) => {
    const current = rp?.highlights || [];
    const next = current.includes(id)
      ? current.filter(h => h !== id)
      : [...current, id];
    update('highlights', next);
  };

  const toggleFaq = (id) => {
    const current = rp?.faq_enabled || {};
    update('faq_enabled', { ...current, [id]: !current[id] });
  };

  const updateFaqAnswer = (id, answer) => {
    const current = rp?.faq_custom_answers || {};
    update('faq_custom_answers', { ...current, [id]: answer });
  };

  // Build live preview of first-contact template
  const buildPreview = () => {
    if (!rp) return '';
    const tone = rp.tone || 'friendly';
    const prefix = { friendly: 'Dzień dobry', professional: 'Szanowni Państwo', concise: 'Dzień dobry' }[tone] || 'Dzień dobry';
    const offer = rp.offer_one_liner || '';
    const avail = rp.availability_text || '';

    if (offer && avail) {
      return `${prefix} Pani/Panie [Imię], dziękuję za kontakt. ${offer} Najbliższy termin: ${avail}. Chętnie podam szczegóły.`;
    } else if (offer) {
      return `${prefix} Pani/Panie [Imię], dziękuję za kontakt. ${offer} Chętnie podam szczegóły i odpowiem na pytania.`;
    }
    return `${prefix} Pani/Panie [Imię], dziękuję za kontakt. Otrzymałam/em wiadomość i chętnie podam szczegóły.`;
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-[#F47A3B] animate-spin" /></div>;

  const completion = rp?.completion_percent || 0;
  const steps = [
    { label: 'Oferta', desc: 'Jednozdaniowe podsumowanie' },
    { label: 'Wyróżniki', desc: 'Czym się wyróżniasz' },
    { label: 'Styl i FAQ', desc: 'Ton odpowiedzi i pytania' },
  ];

  return (
    <div data-testid="tab-content-responses">
      {/* Section header */}
      <div className="mb-5">
        <h2 className="text-[16px] font-bold text-zinc-900 mb-1">Jak odpowiadasz klientom</h2>
        <p className="text-sm text-zinc-500 mb-2">Gdy klient wyśle zapytanie, system przygotuje gotową odpowiedź dopasowaną do Twojej oferty.</p>
        <div className="flex items-center gap-4 text-xs text-zinc-400">
          <span className="flex items-center gap-1.5"><MessageCircle className="w-3.5 h-3.5 text-[#25D366]" />WhatsApp</span>
          <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-blue-400" />E-mail</span>
          <span className="flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-[#F47A3B]" />Szybkie odpowiedzi</span>
        </div>
      </div>

      {/* Completion + save status */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3 flex-1 mr-4">
          <div className="flex-1">
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs text-zinc-500">Kompletność odpowiedzi</span>
              <span className="text-xs font-bold text-[#F47A3B]">{completion}%</span>
            </div>
            <div className="w-full h-1.5 bg-zinc-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full bg-[#F47A3B] transition-all duration-500" style={{ width: `${completion}%` }} />
            </div>
          </div>
        </div>
        {saving && <span className="flex items-center gap-1 text-xs text-[#F47A3B]"><Loader2 className="w-3 h-3 animate-spin" />Zapisuję...</span>}
        {!saving && completion > 0 && <span className="flex items-center gap-1 text-xs text-emerald-500"><Check className="w-3 h-3" />Zapisano</span>}
      </div>

      {/* Step navigation */}
      <div className="flex gap-1 mb-6" data-testid="response-steps">
        {steps.map((s, i) => (
          <button
            key={i}
            onClick={() => setStep(i)}
            data-testid={`response-step-${i}`}
            className={`flex-1 relative px-3 py-3 rounded-xl text-left transition-all ${
              step === i
                ? 'bg-[#F47A3B]/10 border-2 border-[#F47A3B]/30'
                : 'bg-zinc-50 border-2 border-transparent hover:bg-zinc-100'
            }`}
          >
            <div className="flex items-center gap-2">
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                step === i ? 'bg-[#F47A3B] text-white' : 'bg-zinc-200 text-zinc-500'
              }`}>{i + 1}</span>
              <div>
                <p className={`text-sm font-medium ${step === i ? 'text-[#F47A3B]' : 'text-zinc-600'}`}>{s.label}</p>
                <p className="text-[10px] text-zinc-400">{s.desc}</p>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Step content */}
      <AnimatePresence mode="wait">
        <motion.div key={step} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} transition={{ duration: 0.15 }}>

          {/* STEP 0: Oferta */}
          {step === 0 && (
            <div className="space-y-5">
              <Field label="Twoja oferta w jednym zdaniu" why="To zdanie trafi bezpośrednio do pierwszej odpowiedzi dla klienta. Im konkretniej, tym lepiej.">
                <Input
                  value={rp?.offer_one_liner}
                  onChange={v => update('offer_one_liner', v)}
                  placeholder={isHodowla
                    ? 'np. Hodujemy labradory z linii wystawowych z pełnym pakietem badań.'
                    : 'np. Profesjonalny grooming dla wszystkich ras z dojazdem.'}
                  data-testid="field-offer-oneliner"
                />
              </Field>
              <Field label="Dostępność / termin" why="Klient chce wiedzieć kiedy może się spotkać lub odebrać szczeniaka.">
                <Input
                  value={rp?.availability_text}
                  onChange={v => update('availability_text', v)}
                  placeholder={isHodowla
                    ? 'np. Szczeniaki dostępne od połowy kwietnia 2026'
                    : 'np. Najbliższy wolny termin: przyszły tydzień'}
                  data-testid="field-availability"
                />
              </Field>

              {/* Live preview */}
              <div className="mt-2 p-4 bg-zinc-900 rounded-xl" data-testid="reply-preview">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-3.5 h-3.5 text-[#F47A3B]" />
                  <span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wider">Tak może wyglądać odpowiedź do klienta</span>
                </div>
                <p className="text-sm text-zinc-200 leading-relaxed">{buildPreview()}</p>
              </div>
            </div>
          )}

          {/* STEP 1: Wyróżniki */}
          {step === 1 && (
            <div className="space-y-5">
              <p className="text-sm text-zinc-500 mb-1">Te elementy pojawią się w Twoich odpowiedziach i pomogą przekonać klienta do wyboru właśnie Ciebie.</p>
              <div className="grid grid-cols-2 gap-2" data-testid="highlights-grid">
                {(rp?.highlight_options || []).map(opt => {
                  const active = (rp?.highlights || []).includes(opt.id);
                  return (
                    <button
                      key={opt.id}
                      onClick={() => toggleHighlight(opt.id)}
                      data-testid={`highlight-${opt.id}`}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all border-2 ${
                        active
                          ? 'bg-[#F47A3B]/10 border-[#F47A3B]/30 text-zinc-900'
                          : 'bg-white border-zinc-100 text-zinc-500 hover:border-zinc-200'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 transition-colors ${
                        active ? 'bg-[#F47A3B] text-white' : 'bg-zinc-100'
                      }`}>
                        {active && <Check className="w-3 h-3" />}
                      </div>
                      <span className="text-sm font-medium">{opt.label}</span>
                    </button>
                  );
                })}
              </div>
              <Field label="Własny wyróżnik" why="Coś specyficznego, np. 'Jedyna hodowla w Polsce z certyfikatem XYZ'">
                <Input
                  value={rp?.highlight_custom}
                  onChange={v => update('highlight_custom', v)}
                  placeholder="Wpisz swój unikalny wyróżnik"
                  data-testid="field-highlight-custom"
                />
              </Field>
            </div>
          )}

          {/* STEP 2: Ton + FAQ */}
          {step === 2 && (
            <div className="space-y-6">
              {/* Tone selector */}
              <div>
                <label className="text-sm font-medium text-zinc-900 mb-1.5 block">Ton odpowiedzi</label>
                <p className="text-xs text-zinc-500 mb-3">Jak system powinien pisać w Twoim imieniu?</p>
                <div className="flex gap-2" data-testid="tone-selector">
                  {[
                    { id: 'friendly', label: 'Przyjazny', desc: 'Ciepły, bezpośredni', example: 'Dzień dobry Pani Anno...' },
                    { id: 'professional', label: 'Profesjonalny', desc: 'Formalny, rzeczowy', example: 'Szanowni Państwo...' },
                    { id: 'concise', label: 'Zwięzły', desc: 'Krótko i na temat', example: 'Dzień dobry. Dziękuję...' },
                  ].map(t => {
                    const active = (rp?.tone || 'friendly') === t.id;
                    return (
                      <button
                        key={t.id}
                        onClick={() => update('tone', t.id)}
                        data-testid={`tone-${t.id}`}
                        className={`flex-1 p-4 rounded-xl text-left transition-all border-2 ${
                          active
                            ? 'bg-[#F47A3B]/10 border-[#F47A3B]/30'
                            : 'bg-white border-zinc-100 hover:border-zinc-200'
                        }`}
                      >
                        <p className={`text-sm font-semibold ${active ? 'text-[#F47A3B]' : 'text-zinc-700'}`}>{t.label}</p>
                        <p className="text-[11px] text-zinc-400 mt-0.5">{t.desc}</p>
                        <p className="text-[10px] text-zinc-300 mt-1 italic">"{t.example}"</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* FAQ toggles */}
              <div>
                <label className="text-sm font-medium text-zinc-900 mb-1.5 block">Gotowe odpowiedzi na FAQ</label>
                <p className="text-xs text-zinc-500 mb-3">Włącz pytania, na które chcesz mieć gotową odpowiedź. Możesz edytować treść.</p>
                <div className="space-y-2" data-testid="faq-section">
                  {(rp?.faq_presets || []).map(faq => {
                    const enabled = (rp?.faq_enabled || {})[faq.id];
                    const customAnswer = (rp?.faq_custom_answers || {})[faq.id];
                    return (
                      <div key={faq.id} className={`rounded-xl border-2 transition-all ${enabled ? 'border-[#F47A3B]/20 bg-[#F47A3B]/5' : 'border-zinc-100 bg-white'}`}>
                        <button
                          onClick={() => toggleFaq(faq.id)}
                          data-testid={`faq-toggle-${faq.id}`}
                          className="w-full flex items-center justify-between px-4 py-3"
                        >
                          <span className={`text-sm font-medium ${enabled ? 'text-zinc-900' : 'text-zinc-500'}`}>{faq.question}</span>
                          {enabled
                            ? <ToggleRight className="w-6 h-6 text-[#F47A3B] flex-shrink-0" />
                            : <ToggleLeft className="w-6 h-6 text-zinc-300 flex-shrink-0" />
                          }
                        </button>
                        {enabled && (
                          <div className="px-4 pb-3">
                            <textarea
                              value={customAnswer || faq.default_answer}
                              onChange={e => updateFaqAnswer(faq.id, e.target.value)}
                              data-testid={`faq-answer-${faq.id}`}
                              rows={2}
                              className="w-full bg-white border border-zinc-200 rounded-lg px-3 py-2 text-sm text-zinc-700 placeholder-zinc-400 focus:border-[#F47A3B]/50 focus:ring-1 focus:ring-[#F47A3B]/20 outline-none transition resize-none"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Step navigation buttons */}
      <div className="flex justify-between mt-6 pt-4 border-t border-zinc-100">
        <Button
          variant="outline"
          onClick={() => setStep(s => s - 1)}
          disabled={step === 0}
          className="rounded-xl border-zinc-200 text-zinc-500"
          data-testid="response-prev-btn"
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> Wstecz
        </Button>
        {step < 2 ? (
          <Button
            onClick={() => setStep(s => s + 1)}
            className="rounded-xl bg-[#F47A3B] hover:bg-[#E06A2E] text-white"
            data-testid="response-next-btn"
          >
            Dalej <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <div className="flex items-center gap-2">
            {completion >= 80 && <span className="text-xs text-emerald-500 font-medium flex items-center gap-1"><Check className="w-3.5 h-3.5" />System może teraz przygotowywać lepsze odpowiedzi</span>}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── LITTERS INLINE (embedded in Oferta/Mioty tab) ─── */
function LittersInline() {
  const navigate = useNavigate();
  const [litters, setLitters] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/litters`, { headers })
      .then(r => setLitters(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const deleteLitter = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunąć ten miot?')) return;
    try {
      await axios.delete(`${API}/litters/${id}`, { headers });
      setLitters(prev => prev.filter(l => l.id !== id));
    } catch {
      // silently fail
    }
  };

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 text-[#F47A3B] animate-spin" /></div>;

  return (
    <div data-testid="litters-inline">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-zinc-900 flex items-center gap-2">
          <Baby className="w-4 h-4 text-[#F47A3B]" /> Mioty
        </h3>
        {litters.length > 0 && (
          <Button onClick={() => navigate(miotCreate())} size="sm" className="bg-[#F47A3B] hover:bg-[#E06A2B] text-white rounded-xl text-xs" data-testid="add-litter-inline-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Dodaj miot
          </Button>
        )}
      </div>
      {litters.length === 0 ? (
        <div className="p-6 bg-zinc-50 rounded-xl text-center border border-zinc-100" data-testid="litters-empty-inline">
          <Baby className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
          <p className="text-sm text-zinc-500 mb-3">Dodaj mioty, aby przyciągnąć zainteresowanych szczeniętami.</p>
          <Button onClick={() => navigate(miotCreate())} size="sm" className="bg-[#F47A3B] hover:bg-[#E06A2B] text-white rounded-xl text-xs" data-testid="add-first-litter-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Dodaj pierwszy miot
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {litters.map(litter => (
            <div key={litter.id} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-zinc-200 hover:shadow-sm transition" data-testid={`litter-inline-${litter.id}`}>
              <div className="w-14 h-14 rounded-lg bg-zinc-100 overflow-hidden flex-shrink-0">
                {litter.cover_image_url ? (
                  <img src={getImageUrl(litter.cover_image_url)} alt={litter.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><Baby className="w-6 h-6 text-zinc-300" /></div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-zinc-900 truncate">{litter.name}</p>
                <div className="flex items-center gap-3 text-xs text-zinc-500">
                  {litter.breed && <span>{litter.breed}</span>}
                  {litter.birth_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(litter.birth_date).toLocaleDateString('pl-PL')}</span>}
                  {litter.puppies_count > 0 && <span>{litter.puppies_count} szczeniąt</span>}
                </div>
              </div>
              <Badge className={`text-[10px] flex-shrink-0 ${
                litter.status === 'available' ? 'bg-green-100 text-green-700' :
                litter.status === 'reserved' ? 'bg-yellow-100 text-yellow-700' :
                'bg-zinc-100 text-zinc-600'
              }`}>
                {litter.status === 'available' ? 'Dostępne' : litter.status === 'reserved' ? 'Zarezerwowane' : litter.status === 'sold' ? 'Sprzedane' : litter.status || 'Draft'}
              </Badge>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button onClick={() => navigate(miotEdit(litter.id))} className="p-1.5 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg transition" data-testid={`edit-litter-${litter.id}`}>
                  <Edit className="w-4 h-4" />
                </button>
                <button onClick={() => deleteLitter(litter.id)} className="p-1.5 text-zinc-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition" data-testid={`delete-litter-${litter.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── DOGS INLINE (embedded in Psy tab) ─── */
function DogsInline() {
  const navigate = useNavigate();
  const [kennels, setKennels] = useState([]);
  const [dogs, setDogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadData = async () => {
    try {
      const kennelRes = await axios.get(`${API}/kennels`, { headers });
      const k = kennelRes.data || [];
      setKennels(k);
      if (k.length > 0) {
        const dogsRes = await axios.get(`${API}/kennels/${k[0].id}/dogs`, { headers });
        setDogs(dogsRes.data || []);
      }
    } catch { /* */ }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line

  const ensureKennel = async () => {
    setCreating(true);
    try {
      const res = await axios.post(`${API}/kennels/auto-create`, {}, { headers });
      const newKennel = res.data;
      setKennels([newKennel]);
      return newKennel.id;
    } catch {
      toast.error('Nie udało się utworzyć hodowli. Uzupełnij profil.');
      return null;
    } finally {
      setCreating(false);
    }
  };

  const handleAddDog = async () => {
    let kid = kennels[0]?.id;
    if (!kid) {
      kid = await ensureKennel();
    }
    if (kid) {
      navigate(`/wizard/pies/${kid}`);
    }
  };

  const deleteDog = async (dog) => {
    if (!window.confirm(`Czy na pewno chcesz usunąć ${dog.name}?`)) return;
    try {
      await axios.delete(`${API}/dogs/${dog.id}`, { headers });
      setDogs(prev => prev.filter(d => d.id !== dog.id));
    } catch { /* */ }
  };

  const toggleVisibility = async (dog) => {
    try {
      const newStatus = dog.is_hidden || dog.status === 'hidden' ? 'active' : 'hidden';
      await axios.patch(`${API}/dogs/${dog.id}`, { status: newStatus }, { headers });
      setDogs(prev => prev.map(d => d.id === dog.id ? { ...d, status: newStatus, is_hidden: newStatus === 'hidden' } : d));
    } catch { /* */ }
  };

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 text-[#F47A3B] animate-spin" /></div>;

  const kennelId = kennels[0]?.id;

  return (
    <div data-testid="dogs-inline">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[16px] font-bold text-zinc-900 flex items-center gap-2">
          <PawPrint className="w-5 h-5 text-[#F47A3B]" /> Twoje psy
        </h3>
        {dogs.length > 0 && (
          <Button onClick={handleAddDog} disabled={creating} size="sm" className="bg-[#F47A3B] hover:bg-[#E06A2B] text-white rounded-xl text-xs" data-testid="add-dog-inline-btn">
            {creating ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />} Dodaj psa
          </Button>
        )}
      </div>

      {dogs.length === 0 ? (
        <div className="p-6 bg-zinc-50 rounded-xl text-center border border-zinc-100" data-testid="dogs-empty-inline">
          <Dog className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
          <p className="text-sm text-zinc-500 mb-3">Dodaj psy — klienci chcą widzieć rodziców szczeniąt.</p>
          <Button onClick={handleAddDog} disabled={creating} size="sm" className="bg-[#F47A3B] hover:bg-[#E06A2B] text-white rounded-xl text-xs" data-testid="add-first-dog-btn">
            {creating ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />} Dodaj pierwszego psa
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {dogs.map(dog => {
            const isHidden = dog.is_hidden || dog.status === 'hidden';
            const genderSymbol = dog.gender === 'female' ? '♀' : '♂';
            const genderColor = dog.gender === 'female' ? 'text-pink-500' : 'text-blue-500';
            return (
              <div key={dog.id} className={`flex items-center gap-3 p-3 bg-white rounded-xl border border-zinc-200 hover:shadow-sm transition ${isHidden ? 'opacity-60' : ''}`} data-testid={`dog-inline-${dog.id}`}>
                <div className="w-14 h-14 rounded-lg bg-zinc-100 overflow-hidden flex-shrink-0">
                  {dog.main_photo ? (
                    <img src={getImageUrl(dog.main_photo)} alt={dog.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><Dog className="w-6 h-6 text-zinc-300" /></div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-zinc-900 truncate flex items-center gap-1.5">
                    {dog.name} <span className={`text-base ${genderColor}`}>{genderSymbol}</span>
                  </p>
                  <p className="text-xs text-zinc-500">{dog.breed}</p>
                </div>
                <Badge className={`text-[10px] flex-shrink-0 ${isHidden ? 'bg-zinc-100 text-zinc-500' : 'bg-emerald-50 text-emerald-600'}`}>
                  {isHidden ? 'Ukryty' : 'Aktywny'}
                </Badge>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="p-1.5 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg transition">
                      <MoreVertical className="w-4 h-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44 bg-white">
                    <DropdownMenuItem onClick={() => navigate(`/panel/pies/${dog.id}/edytuj`)}>
                      <Edit className="w-4 h-4 mr-2" /> Edytuj
                    </DropdownMenuItem>
                    {dog.slug && (
                      <DropdownMenuItem onClick={() => window.open(`/pies/${dog.slug}`, '_blank')}>
                        <ExternalLink className="w-4 h-4 mr-2" /> Zobacz stronę
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => toggleVisibility(dog)}>
                      {isHidden ? <><Eye className="w-4 h-4 mr-2" /> Pokaż</> : <><EyeOff className="w-4 h-4 mr-2" /> Ukryj</>}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => deleteDog(dog)} className="text-red-600 focus:text-red-600">
                      <Trash2 className="w-4 h-4 mr-2" /> Usuń
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─── STUDS INLINE (embedded in Reproduktory tab) — reuse db.dogs (listing_type=stud) ─── */
function StudsInline() {
  const navigate = useNavigate();
  const [studs, setStuds] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/studs`, { headers })
      .then(r => setStuds(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const deleteStud = async (stud) => {
    if (!window.confirm(`Czy na pewno chcesz usunąć ${stud.name}?`)) return;
    try {
      await axios.delete(`${API}/studs/${stud.id}`, { headers });
      setStuds(prev => prev.filter(s => s.id !== stud.id));
    } catch { /* */ }
  };

  const toggleVisibility = async (stud) => {
    const isHidden = stud.is_hidden || stud.status === 'hidden';
    try {
      await axios.patch(`${API}/studs/${stud.id}/visibility`, { is_hidden: !isHidden }, { headers });
      setStuds(prev => prev.map(s => s.id === stud.id ? { ...s, is_hidden: !isHidden } : s));
    } catch { /* */ }
  };

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 text-[#F47A3B] animate-spin" /></div>;

  return (
    <div data-testid="studs-inline">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[16px] font-bold text-zinc-900 flex items-center gap-2">
          <Crown className="w-5 h-5 text-[#F47A3B]" /> Reproduktory
        </h3>
        {studs.length > 0 && (
          <Button onClick={() => navigate('/wizard/reproduktor')} size="sm" className="bg-[#F47A3B] hover:bg-[#E06A2B] text-white rounded-xl text-xs" data-testid="add-stud-inline-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Dodaj reproduktora
          </Button>
        )}
      </div>

      {studs.length === 0 ? (
        <div className="p-6 bg-zinc-50 rounded-xl text-center border border-zinc-100" data-testid="studs-empty-inline">
          <Crown className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
          <p className="text-sm text-zinc-500 mb-3">Dodaj reproduktory — to magnes na zapytania o krycie.</p>
          <Button onClick={() => navigate('/wizard/reproduktor')} size="sm" className="bg-[#F47A3B] hover:bg-[#E06A2B] text-white rounded-xl text-xs" data-testid="add-first-stud-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Dodaj pierwszego reproduktora
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {studs.map(stud => {
            const isHidden = stud.is_hidden || stud.status === 'hidden';
            return (
              <div key={stud.id} className={`flex items-center gap-3 p-3 bg-white rounded-xl border border-zinc-200 hover:shadow-sm transition ${isHidden ? 'opacity-60' : ''}`} data-testid={`stud-inline-${stud.id}`}>
                <div className="w-14 h-14 rounded-lg bg-zinc-100 overflow-hidden flex-shrink-0">
                  {stud.main_photo ? (
                    <img src={getImageUrl(stud.main_photo)} alt={stud.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><Crown className="w-6 h-6 text-zinc-300" /></div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-zinc-900 truncate flex items-center gap-1.5">
                    {stud.name} <span className="text-base text-blue-500">♂</span>
                  </p>
                  <div className="flex items-center gap-2 text-xs text-zinc-500">
                    <span className="truncate">{stud.breed}</span>
                    {stud.stud_available && <span className="px-1.5 py-0.5 bg-emerald-50 text-emerald-600 rounded text-[10px] font-medium flex-shrink-0">Dostępny</span>}
                  </div>
                </div>
                <Badge className={`text-[10px] flex-shrink-0 ${isHidden ? 'bg-zinc-100 text-zinc-500' : 'bg-emerald-50 text-emerald-600'}`}>
                  {isHidden ? 'Ukryty' : 'Aktywny'}
                </Badge>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="p-1.5 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg transition">
                      <MoreVertical className="w-4 h-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44 bg-white">
                    <DropdownMenuItem onClick={() => navigate(`/wizard/reproduktor/${stud.id}`)}>
                      <Edit className="w-4 h-4 mr-2" /> Edytuj
                    </DropdownMenuItem>
                    {stud.slug && (
                      <DropdownMenuItem onClick={() => window.open(`/reproduktory/${stud.slug}`, '_blank')}>
                        <ExternalLink className="w-4 h-4 mr-2" /> Zobacz stronę
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => toggleVisibility(stud)}>
                      {isHidden ? <><Eye className="w-4 h-4 mr-2" /> Pokaż</> : <><EyeOff className="w-4 h-4 mr-2" /> Ukryj</>}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => deleteStud(stud)} className="text-red-600 focus:text-red-600">
                      <Trash2 className="w-4 h-4 mr-2" /> Usuń
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─── BREED MANAGER TAB ─── */
function BreedManager() {
  const [breeds, setBreeds] = useState([]);
  const [primaryBreed, setPrimaryBreed] = useState(null);
  const [popularBreeds, setPopularBreeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newBreed, setNewBreed] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showPicker, setShowPicker] = useState(false);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchBreeds = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/breeds`, { headers });
      setBreeds(res.data.breeds || []);
      setPrimaryBreed(res.data.primary_breed);
      setPopularBreeds(res.data.popular_breeds || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []); // eslint-disable-line

  useEffect(() => { fetchBreeds(); }, [fetchBreeds]);

  const addBreed = async (breedName) => {
    const name = breedName.trim();
    if (!name || breeds.includes(name)) return;
    setAdding(true);
    try {
      await axios.post(`${API}/breeds/add`, { breed: name }, { headers });
      toast.success(`Dodano: ${name}`);
      setNewBreed('');
      setSearchQuery('');
      setShowPicker(false);
      fetchBreeds();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Nie udało się dodać');
    }
    setAdding(false);
  };

  const removeBreed = async (breed) => {
    if (breeds.length <= 1) {
      toast.error('Nie można usunąć ostatniej rasy');
      return;
    }
    try {
      await axios.post(`${API}/breeds/remove`, { breed }, { headers });
      toast.success(`Usunięto: ${breed}`);
      fetchBreeds();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Nie udało się usunąć');
    }
  };

  const setPrimary = async (breed) => {
    try {
      await axios.post(`${API}/breeds/set-primary`, { breed }, { headers });
      toast.success(`${breed} — rasa główna`);
      fetchBreeds();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Nie udało się ustawić');
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-zinc-400" /></div>;

  const filteredPopular = searchQuery.trim()
    ? popularBreeds.filter(b => b.toLowerCase().includes(searchQuery.toLowerCase()) && !breeds.includes(b))
    : popularBreeds.filter(b => !breeds.includes(b));

  return (
    <div data-testid="tab-content-breeds">
      {/* Info banner */}
      <div className="mb-6 p-4 bg-[#F47A3B]/5 border border-[#F47A3B]/10 rounded-xl">
        <p className="text-sm font-medium text-zinc-900 mb-1">Rasy używane są do dopasowania postów i grup</p>
        <p className="text-xs text-zinc-500">Rasa główna jest używana w kampaniach FB i na landing page.</p>
      </div>

      {/* Current breeds */}
      <div className="space-y-2.5 mb-6">
        {breeds.map((breed, i) => {
          const isPrimary = breed === primaryBreed;
          return (
            <motion.div
              key={breed}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className={`flex items-center justify-between p-3.5 rounded-xl border transition-all ${
                isPrimary
                  ? 'bg-[#F47A3B]/5 border-[#F47A3B]/20'
                  : 'bg-white border-zinc-200 hover:border-zinc-300'
              }`}
              data-testid={`breed-item-${i}`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                  isPrimary ? 'bg-[#F47A3B]/10' : 'bg-zinc-50'
                }`}>
                  <Dog className={`w-4.5 h-4.5 ${isPrimary ? 'text-[#F47A3B]' : 'text-zinc-400'}`} />
                </div>
                <div>
                  <span className={`text-sm font-medium ${isPrimary ? 'text-zinc-900' : 'text-zinc-700'}`}>{breed}</span>
                  {isPrimary && (
                    <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#F47A3B]/10 text-[10px] font-bold text-[#F47A3B]">
                      <Crown className="w-2.5 h-2.5" /> główna
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                {!isPrimary && (
                  <button
                    onClick={() => setPrimary(breed)}
                    className="px-2.5 py-1.5 text-[11px] font-medium text-zinc-500 hover:text-[#F47A3B] hover:bg-[#F47A3B]/5 rounded-lg transition-colors"
                    data-testid={`breed-set-primary-${i}`}
                  >
                    Ustaw jako główną
                  </button>
                )}
                {breeds.length > 1 && (
                  <button
                    onClick={() => removeBreed(breed)}
                    className="p-1.5 text-zinc-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    data-testid={`breed-remove-${i}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Add breed button / picker */}
      {!showPicker ? (
        <Button
          onClick={() => setShowPicker(true)}
          variant="outline"
          className="w-full border-dashed border-zinc-300 hover:border-[#F47A3B]/40 text-zinc-500 hover:text-[#F47A3B] rounded-xl h-12"
          data-testid="breed-add-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Dodaj rasę
        </Button>
      ) : (
        <div className="border border-zinc-200 rounded-xl p-4 bg-zinc-50" data-testid="breed-picker">
          {/* Search input */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Szukaj rasy..."
              className="w-full h-10 text-sm rounded-xl border border-zinc-200 bg-white pl-9 pr-4 focus:border-[#F47A3B]/50 focus:ring-1 focus:ring-[#F47A3B]/20 outline-none text-zinc-900 placeholder:text-zinc-400 transition-all"
              autoFocus
              data-testid="breed-search"
            />
          </div>

          {/* Popular breeds grid */}
          <div className="grid grid-cols-2 gap-1.5 max-h-[280px] overflow-y-auto mb-3">
            {filteredPopular.map((breed) => (
              <button
                key={breed}
                onClick={() => addBreed(breed)}
                disabled={adding}
                className="text-left px-3 py-2 text-sm text-zinc-700 hover:bg-[#F47A3B]/5 hover:text-[#F47A3B] rounded-lg transition-colors truncate"
                data-testid={`breed-pick-${breed}`}
              >
                {breed}
              </button>
            ))}
            {filteredPopular.length === 0 && searchQuery.trim() && (
              <p className="col-span-2 text-xs text-zinc-400 text-center py-2">Nie znaleziono — wpisz poniżej</p>
            )}
          </div>

          {/* Custom input */}
          <div className="flex gap-2">
            <input
              value={newBreed}
              onChange={(e) => setNewBreed(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addBreed(newBreed)}
              placeholder="Wpisz nazwę rasy..."
              className="flex-1 h-10 text-sm rounded-xl border border-zinc-200 bg-white px-4 focus:border-[#F47A3B]/50 outline-none text-zinc-900 placeholder:text-zinc-400"
              data-testid="breed-custom-input"
            />
            <Button
              onClick={() => addBreed(newBreed)}
              disabled={adding || !newBreed.trim()}
              className="bg-[#F47A3B] hover:bg-[#E06A2E] text-white rounded-xl px-5"
              data-testid="breed-custom-add"
            >
              {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Dodaj'}
            </Button>
          </div>

          <button
            onClick={() => { setShowPicker(false); setSearchQuery(''); setNewBreed(''); }}
            className="mt-3 text-xs text-zinc-400 hover:text-zinc-600 transition-colors"
          >
            Zamknij
          </button>
        </div>
      )}
    </div>
  );
}

export default function ProfilePanel() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const initialTab = searchParams.get('tab') || 'basic';
  const [activeTab, setActiveTabState] = useState(initialTab);
  const saveTimer = useRef(null);
  const isHodowla = (user?.profile_type || 'hodowla') !== 'biznes';

  const setActiveTab = (tab) => {
    setActiveTabState(tab);
    setSearchParams(tab === 'basic' ? {} : { tab }, { replace: true });
  };

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/profile`, { headers })
      .then(r => setProfile(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  // Auto-save with debounce
  const refreshPosts = async () => {
    try {
      const res = await axios.post(`${API}/profile/refresh-posts`, {}, { headers });
      toast.success(res.data.message);
    } catch {
      toast.error('Nie udało się odświeżyć postów');
    }
  };

  const autoSave = useCallback((updatedProfile) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        const res = await axios.put(`${API}/profile`, updatedProfile, { headers });
        const syncStatus = res.data.sync_status;
        delete res.data.sync_status;
        setProfile(prev => ({ ...prev, ...res.data }));

        if (syncStatus?.changed_critical_fields?.length > 0) {
          if (syncStatus.has_outdated_posts) {
            toast('Zapisano. Zaktualizować gotowe treści?', {
              duration: 8000,
              action: {
                label: 'Zaktualizuj posty',
                onClick: () => refreshPosts(),
              },
            });
          } else {
            toast.success('Zapisano. Strona i odpowiedzi zaktualizowane automatycznie.');
          }
        }
      } catch (e) {
        console.error('Save failed:', e);
      } finally {
        setSaving(false);
      }
    }, 1200);
  }, []); // eslint-disable-line

  const update = useCallback((field, value) => {
    setProfile(prev => {
      const updated = { ...prev, [field]: value };
      autoSave({ [field]: value });
      return updated;
    });
  }, [autoSave]);

  if (loading) {
    return (
      <StudioShell mode="workspace" activeKey="hodowla" contextLabel="Hodowla">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-[#F47A3B] animate-spin" />
        </div>
      </StudioShell>
    );
  }

  const progress = profile?.progress || 0;
  const isNewUser = progress < 30;
  const TABS = getTabs(isHodowla);
  const currentTabData = TABS.find(t => t.id === activeTab);

  return (
    <StudioShell mode="workspace" activeKey="hodowla" contextLabel="Hodowla">
      <div className="px-4 sm:px-6 lg:px-8 py-6">
      <div className="max-w-[1200px] mx-auto">
        {/* Page header — UI FREEZE: lewe wyrównanie */}
        <header className="mb-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="space-y-1">
              <h1 className="text-[var(--apisai-text-primary)] text-3xl sm:text-4xl font-bold tracking-tight leading-tight" data-testid="profile-title">
                {isHodowla ? 'Profil hodowli' : 'Profil firmy'}
              </h1>
              <p className="text-[var(--apisai-text-secondary)] text-sm sm:text-base max-w-2xl">
                {isNewUser
                  ? 'Uzupełnij profil, aby przygotować stronę, promocję i odpowiedzi do klientów.'
                  : 'Informacje z profilu są używane na Twojej stronie, w promocji i odpowiedziach do klientów.'}
              </p>
              {saving && (
                <span className="inline-flex items-center gap-1.5 text-xs text-[var(--apisai-primary)] mt-1">
                  <Loader2 className="w-3 h-3 animate-spin" /> Zapisywanie...
                </span>
              )}
              {!saving && profile && (
                <span className="inline-flex items-center gap-1.5 text-xs text-emerald-500 mt-1">
                  <Check className="w-3 h-3" /> Zapisano
                </span>
              )}
            </div>

            {/* Quick actions */}
            {profile?.slug && (
              <div className="flex flex-wrap gap-2">
                <a
                  href={`/hodowla/${profile.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-medium hover:bg-emerald-100 transition-colors"
                  data-testid="view-public-page"
                >
                  <Eye className="w-3.5 h-3.5" /> Zobacz stronę
                </a>
                <button
                  onClick={() => navigate(`/wizard/hodowla/${profile.kennel_id || profile.id}`)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-blue-700 text-xs font-medium hover:bg-blue-100 transition-colors"
                  data-testid="edit-kennel-wizard"
                >
                  <Edit className="w-3.5 h-3.5" /> Edytuj w kreatorze
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Progress bar */}
        <div className="mb-8 bg-white border border-zinc-200 rounded-2xl p-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)]" data-testid="progress-section">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-zinc-500">Uzupełnienie profilu</span>
            <span className="text-sm font-bold text-[#F47A3B]">{progress}%</span>
          </div>
          <div className="w-full h-2 bg-zinc-100 rounded-full overflow-hidden">
            <motion.div
              className="h-full rounded-full bg-[#F47A3B]"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            />
          </div>
          <p className="text-xs text-zinc-400 mt-2">Uzupełnij profil, aby zwiększyć liczbę zapytań</p>
          {profile?.missing?.length > 0 && progress < 100 && (
            <div className="mt-3 pt-3 border-t border-zinc-100">
              <p className="text-xs font-semibold text-zinc-600 mb-1.5">Brakuje do pełnego profilu:</p>
              <div className="flex flex-wrap gap-1.5">
                {profile.missing.map(m => (
                  <button
                    key={m.field}
                    onClick={() => setActiveTab(m.tab)}
                    className="text-[11px] px-2.5 py-1 rounded-full bg-[#F47A3B]/8 text-[#F47A3B] border border-[#F47A3B]/15 hover:bg-[#F47A3B]/15 transition-colors cursor-pointer"
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          {progress >= 100 && (
            <p className="text-xs font-semibold text-emerald-600 mt-2">Profil kompletny — system pracuje na pełnych obrotach</p>
          )}
        </div>

        {/* Test data warnings */}
        {profile?.test_warnings?.length > 0 && (
          <div className="mb-5 bg-amber-50 border border-amber-200 rounded-2xl p-4 shadow-[0_2px_8px_rgba(0,0,0,0.03)]" data-testid="test-data-warning">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-amber-800 mb-1">Uwaga — wykryliśmy testowe dane</p>
                {profile.test_warnings.map(w => (
                  <p key={w.field} className="text-xs text-amber-700 mb-0.5">
                    {w.message}
                  </p>
                ))}
                <p className="text-[11px] text-amber-500 mt-1.5">Te dane nie będą widoczne w postach i na stronie do momentu poprawienia.</p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* Left: Tabs + Content */}
          <div className="lg:col-span-8 min-w-0">
            {/* Tabs — modern underline style */}
            <div className="flex gap-0.5 mb-6 overflow-x-auto pb-2 border-b border-zinc-200 scrollbar-hide" data-testid="profile-tabs">
              {TABS.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    data-testid={`tab-${tab.id}`}
                    className={`relative flex items-center gap-1.5 px-3 py-2 text-xs font-medium whitespace-nowrap transition ${
                      activeTab === tab.id
                        ? 'text-[#F47A3B]'
                        : 'text-zinc-400 hover:text-zinc-700'
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {tab.label}
                    {activeTab === tab.id && (
                      <motion.div
                        layoutId="profile-tab-underline"
                        className="absolute bottom-0 left-2 right-2 h-[2px] bg-[#F47A3B] rounded-full"
                      />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Tab content */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="bg-white border border-zinc-200 rounded-2xl p-6"
              >
                {/* DANE PODSTAWOWE (merged: basic + o nas + social) */}
                {activeTab === 'basic' && (
                  <div data-testid="tab-content-basic">
                    {profile?.interview_synced && (
                      <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-xl bg-emerald-50 border border-emerald-200" data-testid="interview-synced-banner">
                        <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                        <span className="text-xs text-emerald-700 font-medium">Uzupełnione podczas pierwszego ustawienia — nie musisz wpisywać tego samego</span>
                      </div>
                    )}
                    <Field label="Nazwa działalności" why="Nazwa wyświetlana na stronie i w wynikach wyszukiwania." fromInterview={profile?.interview_fields?.includes('business_name')}>
                      <Input value={profile?.business_name} onChange={v => update('business_name', v)} placeholder="np. Hodowla Złoty Labrador" data-testid="field-business-name" />
                    </Field>
                    <Field label="Typ działalności" fromInterview={profile?.interview_fields?.includes('business_type')}>
                      <select
                        value={profile?.business_type || 'breeder'}
                        onChange={e => update('business_type', e.target.value)}
                        className="w-full bg-white border border-zinc-200 rounded-xl px-4 py-3 text-zinc-900 text-sm focus:border-[#F47A3B]/50 outline-none"
                        data-testid="field-business-type"
                      >
                        <option value="breeder">Hodowca</option>
                        <option value="groomer">Groomer</option>
                        <option value="vet">Weterynarz</option>
                        <option value="trainer">Trener</option>
                        <option value="shop">Sklep zoologiczny</option>
                        <option value="other">Inne</option>
                      </select>
                    </Field>
                    <div className="grid grid-cols-2 gap-4">
                      <Field label="Miasto" fromInterview={profile?.interview_fields?.includes('city')}>
                        <Input value={profile?.city} onChange={v => update('city', v)} placeholder="np. Warszawa" data-testid="field-city" />
                      </Field>
                      <Field label="Województwo">
                        <Input value={profile?.voivodeship} onChange={v => update('voivodeship', v)} placeholder="np. mazowieckie" data-testid="field-voivodeship" />
                      </Field>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <Field label="Telefon" why="Klienci zadzwonią bezpośrednio.">
                        <Input value={profile?.phone} onChange={v => update('phone', v)} placeholder="+48 500 000 000" type="tel" data-testid="field-phone" />
                      </Field>
                      <Field label="Email">
                        <Input value={profile?.email} onChange={v => update('email', v)} placeholder={isHodowla ? "kontakt@hodowla.pl" : "kontakt@firma.pl"} type="email" data-testid="field-email" />
                      </Field>
                    </div>
                    <Field label="WhatsApp" why="Klienci mogą napisać bezpośrednio na WhatsApp — szybki kontakt.">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 rounded-xl bg-[#25D366]/10 flex items-center justify-center flex-shrink-0">
                          <MessageCircle className="w-5 h-5 text-[#25D366]" />
                        </div>
                        <Input value={profile?.whatsapp} onChange={v => update('whatsapp', v)} placeholder="+48 500 000 000" type="tel" data-testid="field-whatsapp" />
                      </div>
                    </Field>

                    {/* O NAS — merged */}
                    <div className="mt-6 pt-6 border-t border-zinc-100">
                      <h3 className="text-sm font-bold text-zinc-900 mb-4 flex items-center gap-2">
                        <FileText className="w-4 h-4 text-[#F47A3B]" /> O nas
                      </h3>
                      <Field label="Opis" why={isHodowla ? "Opowiedz o sobie lub hodowli. To pierwsza rzecz, którą widzą odwiedzający." : "Opowiedz o swojej firmie."}>
                        <div className="flex items-center gap-2 mb-2">
                          <GenerateBtn field="description" profileType={isHodowla ? 'hodowla' : 'biznes'} onGenerated={v => update('description', v)} />
                          <span className="text-[10px] text-zinc-400">Napiszemy za Ciebie</span>
                        </div>
                        <Textarea value={profile?.description} onChange={v => update('description', v)} placeholder={isHodowla ? "Opowiedz o swojej hodowli..." : "Opowiedz o swojej firmie..."} rows={4} data-testid="field-description" />
                      </Field>
                      <Field label="Wyróżniki" why="Co Cię wyróżnia?" fromInterview={profile?.interview_fields?.includes('highlights')}>
                        <TagsInput value={profile?.highlights || []} onChange={v => update('highlights', v)} placeholder="Dodaj wyróżnik i wciśnij Enter" />
                      </Field>
                      <Field label="Opis własnymi słowami" why="To zostanie użyte do generowania postów, landing page i treści reklamowych. Im lepiej opiszesz — tym lepsze posty." fromInterview={profile?.interview_fields?.includes('custom_description')}>
                        <Textarea value={profile?.custom_description} onChange={v => update('custom_description', v)} placeholder={isHodowla ? "Np. Od 15 lat hoduję golden retrievery. Moje psy mają pełne badania..." : "Np. Prowadzę kursy groomerskie od 5 lat. Uczę od podstaw do zaawansowanych technik..."} rows={4} data-testid="field-custom-description" />
                      </Field>
                      <Field label="Co jest największą wartością dla klienta?" why="Pomoże tworzyć mocniejsze posty sprzedażowe." fromInterview={profile?.interview_fields?.includes('client_value')}>
                        <TagsInput value={profile?.client_value || []} onChange={v => update('client_value', v)} placeholder={isHodowla ? "np. Zdrowy pies, Wsparcie po odbiorze" : "np. Nowy zawód, Certyfikat"} />
                      </Field>
                      <Field label="Doświadczenie">
                        <div className="flex items-center gap-2 mb-2">
                          <GenerateBtn field="experience" profileType={isHodowla ? 'hodowla' : 'biznes'} onGenerated={v => update('experience', v)} />
                          <span className="text-[10px] text-zinc-400">Napiszemy za Ciebie</span>
                        </div>
                        <Textarea value={profile?.experience} onChange={v => update('experience', v)} placeholder={isHodowla ? "Np. Hoduję od 2005 roku..." : "Np. Na rynku od 2010 roku..."} rows={3} data-testid="field-experience" />
                      </Field>
                    </div>

                    {/* SOCIAL MEDIA — merged */}
                    <div className="mt-6 pt-6 border-t border-zinc-100">
                      <h3 className="text-sm font-bold text-zinc-900 mb-4 flex items-center gap-2">
                        <Share2 className="w-4 h-4 text-[#F47A3B]" /> Social media
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <Field label="Facebook">
                          <Input value={profile?.social_facebook} onChange={v => update('social_facebook', v)} placeholder="https://facebook.com/..." data-testid="field-facebook" />
                        </Field>
                        <Field label="Instagram">
                          <Input value={profile?.social_instagram} onChange={v => update('social_instagram', v)} placeholder="https://instagram.com/..." data-testid="field-instagram" />
                        </Field>
                        <Field label="TikTok">
                          <Input value={profile?.social_tiktok} onChange={v => update('social_tiktok', v)} placeholder="https://tiktok.com/@..." data-testid="field-tiktok" />
                        </Field>
                        <Field label="Strona WWW">
                          <Input value={profile?.social_website} onChange={v => update('social_website', v)} placeholder="https://twoja-strona.pl" data-testid="field-website" />
                        </Field>
                      </div>
                    </div>
                  </div>
                )}

                {/* RASY — breed management for hodowla */}
                {activeTab === 'breeds' && isHodowla && (
                  <BreedManager />
                )}


                {/* OFERTA / MIOTY */}
                {activeTab === 'offer' && (
                  <div data-testid="tab-content-offer">
                    <Field label="Opis oferty" why={isHodowla ? "Co oferujesz klientom? Szczenięta, krycie, szkolenia?" : "Jakie usługi oferujesz?"}>
                      <div className="flex items-center gap-2 mb-2">
                        <GenerateBtn field="offer_description" profileType={isHodowla ? 'hodowla' : 'biznes'} onGenerated={v => update('offer_description', v)} />
                        <span className="text-[10px] text-zinc-400">Napiszemy za Ciebie</span>
                      </div>
                      <Textarea value={profile?.offer_description} onChange={v => update('offer_description', v)} placeholder={isHodowla ? "Opisz swoją ofertę..." : "Opisz swoje usługi..."} rows={4} data-testid="field-offer" />
                    </Field>

                    {/* MIOTY — embedded */}
                    {isHodowla && (
                      <div className="mt-6 pt-6 border-t border-zinc-100">
                        <LittersInline />
                      </div>
                    )}
                  </div>
                )}

                {/* PSY / SUKI (ZASOBY) — only for hodowla */}
                {activeTab === 'dogs' && isHodowla && (
                  <DogsInline />
                )}

                {/* REPRODUKTORY — only for hodowla (reuse db.dogs listing_type=stud) */}
                {activeTab === 'studs' && isHodowla && (
                  <StudsInline />
                )}

                {/* ODPOWIEDZI (Context Engine) */}
                {activeTab === 'responses' && (
                  <ResponsesTab isHodowla={isHodowla} />
                )}

                {/* GALERIA */}
                {activeTab === 'photos' && (
                  <div data-testid="tab-content-photos">
                    <div className="mb-5 p-4 bg-[#F47A3B]/5 border border-[#F47A3B]/10 rounded-xl">
                      <p className="text-sm font-medium text-zinc-900 mb-1">Zdjęcia zasilają Twoją stronę, posty i odpowiedzi</p>
                      <p className="text-xs text-zinc-500">Dodaj zdjęcia raz — użyjemy ich wszędzie.</p>
                    </div>
                    {profile?.interview_fields?.includes('gallery') && (profile?.gallery?.length > 0) && (
                      <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-xl bg-emerald-50 border border-emerald-200">
                        <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                        <span className="text-xs text-emerald-700 font-medium">Zdjęcia dodane podczas wywiadu — widoczne na stronie i w postach</span>
                      </div>
                    )}
                    <Field label="Zdjęcie główne" why="To zdjęcie jest na górze strony i w wynikach wyszukiwania." fromInterview={profile?.interview_fields?.includes('gallery') && !!profile?.main_photo}>
                      <ProfilePhotoUpload
                        currentUrl={getImageUrl(profile?.main_photo)}
                        onUploaded={url => update('main_photo', url)}
                        testId="field-main-photo"
                      />
                    </Field>
                    <Field label="Galeria" why="Dodatkowe zdjęcia budują zaufanie." fromInterview={profile?.interview_fields?.includes('gallery')}>
                      <GalleryUpload
                        currentUrls={(profile?.gallery || []).map(u => getImageUrl(u)).filter(Boolean)}
                        onChanged={urls => update('gallery', urls)}
                      />
                    </Field>
                  </div>
                )}

                {/* SEO auto-info block */}
                {activeTab === 'basic' && profile?.seo_title && (
                  <div className="mt-4 p-3 bg-zinc-50 border border-zinc-100 rounded-xl">
                    <p className="text-[11px] text-zinc-400 font-medium uppercase tracking-wider mb-1">SEO działa automatycznie</p>
                    <p className="text-xs text-zinc-500">Tytuł: {profile.seo_title}</p>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Right: Dynamic Context Panel */}
          <div className="hidden lg:block lg:col-span-4">
            <div className="sticky top-[100px] space-y-4">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.15 }}
                >
                  {SECTION_CONTEXT[activeTab] && (
                    <div className="bg-white border border-zinc-200 rounded-2xl p-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)]" data-testid="context-panel">
                      <h3 className="text-[15px] font-bold text-zinc-900 mb-2">{SECTION_CONTEXT[activeTab].title}</h3>
                      <p className="text-sm text-zinc-500 mb-4">{SECTION_CONTEXT[activeTab].desc}</p>
                      {SECTION_CONTEXT[activeTab].micro && (
                        <p className="text-xs text-zinc-400 mb-4 italic">{SECTION_CONTEXT[activeTab].micro}</p>
                      )}
                      <div className="space-y-2 mb-4">
                        {SECTION_CONTEXT[activeTab].benefits.map((b, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <div className="w-5 h-5 rounded-full bg-[#F47A3B]/10 flex items-center justify-center flex-shrink-0">
                              <Check className="w-3 h-3 text-[#F47A3B]" />
                            </div>
                            <span className="text-sm text-zinc-600">{b}</span>
                          </div>
                        ))}
                      </div>
                      <div className="p-3 bg-[#F47A3B]/5 border border-[#F47A3B]/10 rounded-xl">
                        <p className="text-[11px] text-zinc-400 font-medium uppercase tracking-wider mb-1">Co teraz</p>
                        <p className="text-sm text-[#F47A3B] font-medium">{SECTION_CONTEXT[activeTab].action}</p>
                      </div>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>

              {/* CTA: See your page */}
              <Link to="/panel/moje" data-testid="preview-page-cta">
                <div className="bg-white border border-zinc-200 rounded-2xl p-4 hover:border-[#F47A3B]/30 transition-colors group shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-[#F47A3B]/10 flex items-center justify-center flex-shrink-0">
                      <Eye className="w-4 h-4 text-[#F47A3B]" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-zinc-900">Zobacz swoją stronę</p>
                      <p className="text-[11px] text-zinc-400">Sprawdź jak wygląda Twoja oferta</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-300 group-hover:text-[#F47A3B] transition-colors" />
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>
      </div>
    </StudioShell>
  );
}
